"use client";

import { useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { WorkspaceSessionOwnerDisplay } from "@/lib/workspace-session-owner";
import { cn } from "@/lib/utils";

export type PersonPresentation = "name" | "avatar";

export function PersonName({
  person,
  className,
  variant = "default",
  presentation = "name",
}: {
  person: WorkspaceSessionOwnerDisplay;
  className?: string;
  variant?: "default" | "mention";
  presentation?: PersonPresentation;
}) {
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const showImage = Boolean(
    person.imageUrl && person.imageUrl !== failedImageUrl,
  );
  const avatar = (
    <span
      aria-hidden
      className={cn(
        "inline-flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-full text-3xs font-semibold",
        variant === "mention"
          ? "border border-link/25 bg-background text-link"
          : "bg-muted text-muted-foreground",
      )}
    >
      {showImage ? (
        <img
          src={person.imageUrl ?? undefined}
          alt=""
          className="size-full object-cover"
          referrerPolicy="no-referrer"
          onError={() => setFailedImageUrl(person.imageUrl)}
        />
      ) : (
        person.initials
      )}
    </span>
  );

  if (presentation === "avatar") {
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              className={cn(
                "inline-flex size-5 shrink-0 align-middle",
                className,
              )}
            />
          }
        >
          {avatar}
          <span className="sr-only">{person.label}</span>
        </TooltipTrigger>
        <TooltipContent sideOffset={6}>{person.label}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex min-w-0 items-center gap-1.5 align-middle",
        className,
      )}
      title={person.label}
    >
      {avatar}
      <span
        className={cn(
          "truncate font-medium",
          variant === "mention" ? "text-link" : "text-foreground",
        )}
      >
        {person.label}
      </span>
    </span>
  );
}

export function PersonAvatarStack({
  people,
  className,
  maxVisible = 3,
}: {
  people: readonly WorkspaceSessionOwnerDisplay[];
  className?: string;
  maxVisible?: number;
}) {
  const visiblePeople = people.slice(0, maxVisible);
  const hiddenPeople = people.slice(maxVisible);
  const hiddenLabel = hiddenPeople.map((person) => person.label).join(", ");

  return (
    <span className={cn("inline-flex items-center -space-x-1", className)}>
      {visiblePeople.map((person, index) => (
        <PersonName
          key={person.ownerId ?? `${person.label}:${index}`}
          person={person}
          presentation="avatar"
          className="rounded-full ring-2 ring-background"
        />
      ))}
      {hiddenPeople.length > 0 ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-3xs font-semibold text-muted-foreground ring-2 ring-background" />
            }
          >
            <span aria-hidden>+{hiddenPeople.length}</span>
            <span className="sr-only">
              {hiddenPeople.length} more: {hiddenLabel}
            </span>
          </TooltipTrigger>
          <TooltipContent sideOffset={6}>{hiddenLabel}</TooltipContent>
        </Tooltip>
      ) : null}
    </span>
  );
}
